import { useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { APP_VERSION } from "@/lib/appVersion";

// Stale-bundle detector. An open tab keeps running the JS it loaded — which, after
// a deploy, can be OLD client code talking to NEW edge functions (the workout
// "3 ACKs, 0 writes" class of skew). This polls the build manifest (/version.json,
// emitted by the vite versionManifestPlugin) and flips `stale` when the published
// version no longer matches the one THIS bundle was built with.
//
// Triggers: on mount, whenever the tab regains visibility (the common "left it
// open" case), and a slow interval fallback. Web-only: native bundles ship via
// the App Store and have no server manifest to poll; "dev" has nothing published.

const POLL_INTERVAL_MS = 15 * 60 * 1000;

// One-shot auto-reload guard. sessionStorage is per-tab: each stale tab gets
// exactly ONE controlled reload; if the reload doesn't converge (e.g. a deploy
// still propagating), the key stays set and we fall back to the banner —
// never a reload loop.
const AUTORELOAD_KEY = "vyv-stale-autoreload";

export function useVersionCheck(): { stale: boolean } {
  const [stale, setStale] = useState(false);

  useEffect(() => {
    let isNative = false;
    try { isNative = Capacitor.isNativePlatform(); } catch { /* web */ }
    if (isNative || APP_VERSION === "dev") return;

    let active = true;
    const check = async (allowReload = false) => {
      try {
        const res = await fetch("/version.json?t=" + Date.now(), { cache: "no-store" });
        if (!res.ok) return;
        const data = (await res.json()) as { version?: string };
        if (active && data.version && data.version !== APP_VERSION) {
          setStale(true);
          // Auto-heal the "left the tab open across a deploy" case: reload
          // once, and ONLY on the visibility-return trigger — the user just
          // switched back, so we never interrupt an in-progress interaction.
          if (allowReload) {
            try {
              if (!sessionStorage.getItem(AUTORELOAD_KEY)) {
                sessionStorage.setItem(AUTORELOAD_KEY, APP_VERSION || "1");
                window.location.reload();
              }
            } catch {
              // sessionStorage unavailable — banner remains the fallback.
            }
          }
        }
      } catch {
        // offline / transient — ignore; the next trigger re-checks.
      }
    };

    void check();
    const onVisible = () => {
      if (document.visibilityState === "visible") void check(true);
    };
    document.addEventListener("visibilitychange", onVisible);
    const id = window.setInterval(check, POLL_INTERVAL_MS);
    return () => {
      active = false;
      document.removeEventListener("visibilitychange", onVisible);
      window.clearInterval(id);
    };
  }, []);

  return { stale };
}
